/*
 * Copyright 2026 Patched Reality, Inc.
 * SPDX-License-Identifier: Apache-2.0
 */

import * as THREE from 'three';
import { OrbitControls } from 'three/addons/controls/OrbitControls.js';
import { GLTFLoader } from 'three/addons/loaders/GLTFLoader.js';
import { DRACOLoader } from 'three/addons/loaders/DRACOLoader.js';
import { KTX2Loader } from 'three/addons/loaders/KTX2Loader.js';
import { RoomEnvironment } from 'three/addons/environments/RoomEnvironment.js';
import { Sky } from 'three/addons/objects/Sky.js';
import Hls from 'hls.js';
import { createSkyDome, createStarfield, createInfiniteGrid, calculateGridSpacing, updateGridSpacing } from './scene-helpers.js';
import { resolveResourceUrl } from '../lib/ManifolderClient/node-helpers.js';
import { NodeAdapter } from './node-adapter.js';
import { calculateSunPosition, getSunLightingParams, calculateLatLong } from './geo-utils.js';
import { NODE_COLORS } from '../shared/node-types.js';

// Ownership lives on the assets shared by cached templates and their clones.
const cachedAssetOwner = Symbol('cachedAssetOwner');

export class ViewResource {
  constructor(containerSelector, stateManager, model) {
    this.container = document.querySelector(containerSelector);
    this.stateManager = stateManager;
    this.model = model;
    this.scene = null;
    this.camera = null;
    this.renderer = null;
    this.controls = null;

    this.gltfLoader = new GLTFLoader();

    // Set up Draco loader for compressed geometry (store for disposal)
    this.dracoLoader = new DRACOLoader();
    this.dracoLoader.setDecoderPath('https://www.gstatic.com/draco/versioned/decoders/1.5.6/');
    this.gltfLoader.setDRACOLoader(this.dracoLoader);
    this.loadedModels = [];
    this.contentGroup = null;
    this.rotators = [];  // Active rotator animations
    this.videoPlanes = [];  // Video meshes for click-to-play
    this.hlsInstances = [];  // HLS.js instances for cleanup
    this.clock = new THREE.Clock();

    this.metadataCache = new Map();
    this.glbCache = new Map();

    this.raycaster = new THREE.Raycaster();
    this.mouse = new THREE.Vector2();

    this.nodeGroups = new Map();
    this.nodeResourceGroups = new Map();
    this._fetchSemaphore = { active: 0, limit: 6, queue: [] };

    this.currentResourceUrl = null;
    this.currentRootKey = null; // Scoped node keys contain colons; keep root identity separately.
    this.currentNode = null;
    this.isLoading = false;
    this.loadRequestId = 0;  // Increments on each load to handle race conditions

    this.showBounds = false;
    this.boundsGroup = null;

    // Sun lighting state
    this.location = { latitude: 0, longitude: 0 }; // Default: equator/prime meridian
    this.timeOffset = 0; // Hours offset from current time
    this.isEarthBased = false;

    this.statusCallbacks = [];

    this.animationFrameId = null;
    this.disposed = false;
    this.initialized = false;
    this._wasHidden = false;

    this._bindModelEvents();
    this.init();
    if (this.initialized) {
      this.animate();
    }
  }

  _bindModelEvents() {
    this.model.on('selectionChanged', (node) => {
      if (!node) return;

      const planetContext = this.model.getPlanetContext(node);
      if (node.worldPos && planetContext?.radius) {
        const coords = calculateLatLong(node.worldPos, planetContext.radius);
        if (coords) {
          this.setLocation(coords.latitude, coords.longitude);
        } else {
          this.setLocation(0, 0);
        }
      } else {
        this.setLocation(0, 0);
      }

      this.setNode(node);
    });

    this.model.on('dataChanged', () => {
      this._refreshIfSelected();
    });

    this.model.on('nodeUpdated', (node) => {
      const selected = this.model.getSelectedNode();
      if (selected && node === selected && node.worldPos) {
        const planetContext = this.model.getPlanetContext(node);
        if (planetContext?.radius) {
          const coords = calculateLatLong(node.worldPos, planetContext.radius);
          if (coords) {
            this.setLocation(coords.latitude, coords.longitude);
          }
        }
        this.setNode(node);
      }
    });

    this.model.on('nodeVisibilityChanged', (node, hidden) => {
      const nodeKey = this.model.nodeKey(node);
      const resourceGroup = this.nodeResourceGroups.get(nodeKey);
      if (resourceGroup) {
        resourceGroup.visible = !hidden;
      }
    });
  }

  init() {
    if (!this.container) {
      console.error('ViewResource: Container not found');
      return;
    }

    this.scene = new THREE.Scene();
    this.scene.background = new THREE.Color(0x0c0c14);

    const width = this.container.clientWidth || 800;
    const height = this.container.clientHeight || 600;

    this.camera = new THREE.PerspectiveCamera(60, width / height, 0.1, 100000);
    this.camera.position.set(50, 30, 50);

    this.renderer = new THREE.WebGLRenderer({ antialias: true, logarithmicDepthBuffer: true });
    this.renderer.setSize(width, height);
    this.renderer.setPixelRatio(window.devicePixelRatio);
    this.renderer.outputColorSpace = THREE.SRGBColorSpace;
    this.renderer.toneMapping = THREE.ACESFilmicToneMapping;
    this.renderer.toneMappingExposure = .75;
    this.renderer.shadowMap.enabled = true;
    this.renderer.shadowMap.type = THREE.PCFSoftShadowMap;
    this.container.appendChild(this.renderer.domElement);

    // Environment map for soft IBL lighting
    this.pmremGenerator = new THREE.PMREMGenerator(this.renderer);
    this.pmremGenerator.compileEquirectangularShader();
    const roomEnv = new RoomEnvironment();
    this.scene.environment = this.pmremGenerator.fromScene(roomEnv).texture;
    roomEnv.dispose();

    this.controls = new OrbitControls(this.camera, this.renderer.domElement);
    this.controls.enableDamping = true;
    this.controls.dampingFactor = 0.2;

    // Late afternoon sunlight (shadow camera frustum set dynamically in _fitShadowCamera)
    this.keyLight = new THREE.DirectionalLight(0xffebd6, 2.1);
    this.keyLight.position.set(85, 52, 50);
    this.keyLight.castShadow = true;
    this.keyLight.shadow.mapSize.width = 4096;
    this.keyLight.shadow.mapSize.height = 4096;
    this.keyLight.shadow.bias = -0.0005;
    this.scene.add(this.keyLight);
    this.scene.add(this.keyLight.target);

    this.scene.add(this.camera);

    // Set up KTX2 loader for compressed textures (store for disposal)
    this.ktx2Loader = new KTX2Loader();
    this.ktx2Loader.setTranscoderPath('https://www.gstatic.com/basis-universal/versioned/2021-04-15-ba1c3e4/');
    this.ktx2Loader.detectSupport(this.renderer);
    this.gltfLoader.setKTX2Loader(this.ktx2Loader);

    this.gridHelper = createInfiniteGrid(this.scene);

    // Three.js Sky for daytime atmospheric scattering
    this.sky = new Sky();
    this.sky.scale.setScalar(450000);
    this.sky.material.depthWrite = false;
    this.sky.material.depthTest = false;
    this.sky.renderOrder = -1000;
    this.scene.add(this.sky);

    // Sky parameters - adjusted dynamically in updateSunLighting based on elevation
    const skyUniforms = this.sky.material.uniforms;
    skyUniforms.mieCoefficient.value = 0.005;
    skyUniforms.mieDirectionalG.value = 0.7;

    // Night sky - gradient dome and starfield
    this.skyDome = createSkyDome(this.scene);
    this.skyDome.visible = false;
    this.starfield = createStarfield(this.scene, { radius: 80000 });
    this.starfield.visible = false;

    this.setupTimeSlider();
    this.setResourceMode(false);

    // Invisible ground plane that only shows shadows
    const shadowPlaneGeo = new THREE.PlaneGeometry(2000, 2000);
    shadowPlaneGeo.rotateX(-Math.PI / 2);
    this.shadowPlane = new THREE.Mesh(shadowPlaneGeo, new THREE.ShadowMaterial({ opacity: 0.4 }));
    this.shadowPlane.receiveShadow = true;
    this.scene.add(this.shadowPlane);

    // Store bound handlers for cleanup
    this.boundResizeHandler = () => this.onWindowResize();
    this.boundVisibilityHandler = () => this.onWindowResize();
    this.boundDblClickHandler = (e) => this.onDoubleClick(e);
    this.boundClickHandler = (e) => this.onClick(e);

    window.addEventListener('resize', this.boundResizeHandler);
    document.addEventListener('visibilitychange', this.boundVisibilityHandler);
    this.setupEventListeners();
    this.setupBoundsToggle();
    this.initialized = true;
  }

  setupEventListeners() {
    this.renderer.domElement.addEventListener('dblclick', this.boundDblClickHandler);
    this.renderer.domElement.addEventListener('click', this.boundClickHandler);
  }

  setupTimeSlider() {
    // Calculate current solar time at location to set initial slider position
    const now = new Date();
    const currentSolarHours = (now.getUTCHours() + now.getUTCMinutes() / 60 + this.location.longitude / 15 + 24) % 24;
    // Slider is 0-24 starting from 8am, so convert current time to slider value
    const sliderValue = (currentSolarHours - 8 + 24) % 24;

    this.timeSlider = document.getElementById('time-slider');
    this.timeHourInput = document.getElementById('time-hour');
    this.timeMinuteInput = document.getElementById('time-minute');

    if (this.timeSlider) {
      this.timeSlider.value = sliderValue.toFixed(4);
      this.timeSliderValue = sliderValue;

      this.boundTimeSliderHandler = () => {
        this.timeSliderValue = parseFloat(this.timeSlider.value);
        this.updateSunLighting();
      };
      this.timeSlider.addEventListener('input', this.boundTimeSliderHandler);
    }

    if (this.timeHourInput && this.timeMinuteInput) {
      this.boundTimeInputHandler = () => {
        const h = parseInt(this.timeHourInput.value) || 0;
        const m = parseInt(this.timeMinuteInput.value) || 0;
        const solarHours = h + m / 60;
        this.timeSliderValue = (solarHours - 8 + 24) % 24;
        if (this.timeSlider) {
          this.timeSlider.value = this.timeSliderValue.toFixed(4);
        }
        this.updateSunLighting();
      };

      this.timeHourInput.addEventListener('change', this.boundTimeInputHandler);
      this.timeMinuteInput.addEventListener('change', this.boundTimeInputHandler);
    }

  }

  updateSunLighting() {
    // Calculate time: slider goes 0-24 hours starting from 8am
    const sliderHours = this.timeSliderValue ?? 4;
    const solarHours = (8 + sliderHours) % 24;

    // Create a date for sun position calculation
    const now = new Date();
    const utcHours = solarHours - this.location.longitude / 15;
    now.setUTCHours(Math.floor(utcHours), (utcHours % 1) * 60, 0, 0);

    const { azimuth, elevation } = calculateSunPosition(
      this.location.latitude,
      this.location.longitude,
      now
    );

    // Position directional light based on sun azimuth/elevation
    const elevRad = elevation * Math.PI / 180;
    const azimRad = azimuth * Math.PI / 180;

    const lightDir = new THREE.Vector3(
      Math.cos(elevRad) * Math.sin(azimRad),
      Math.sin(elevRad),
      Math.cos(elevRad) * Math.cos(azimRad)
    );
    const target = this.keyLight.target.position;
    const distance = this.keyLight.position.distanceTo(target) || 100;
    this.keyLight.position.copy(target).add(lightDir.multiplyScalar(distance));

    // Get lighting params based on sun elevation
    const params = getSunLightingParams(elevation);
    this.keyLight.color.setHex(params.color);
    this.keyLight.intensity = params.intensity;

    // Update Sky addon sun position (phi = zenith angle, theta = azimuth)
    const phi = (90 - elevation) * Math.PI / 180;
    const theta = azimuth * Math.PI / 180;
    const sunPosition = new THREE.Vector3().setFromSphericalCoords(1, phi, theta);
    this.sky.material.uniforms.sunPosition.value.copy(sunPosition);

    // Adjust sky parameters based on sun elevation
    // Keep clear blue sky until sun is very low, then ramp up haze for sunset
    let rayleigh, turbidity;
    if (elevation > 5) {
      // Daytime - clear blue sky
      rayleigh = 0.08;
      turbidity = 0.3;
    } else if (elevation > 0) {
      // Near horizon - ramp up for sunset/sunrise
      const t = (5 - elevation) / 5;
      rayleigh = 0.08 + t * (3 - 0.08);
      turbidity = 0.3 + t * (10 - 0.3);
    } else {
      // Below horizon
      rayleigh = 3;
      turbidity = 10;
    }
    this.sky.material.uniforms.rayleigh.value = rayleigh;
    this.sky.material.uniforms.turbidity.value = turbidity;

    // Day/night/twilight transitions
    if (elevation > 0) {
      // Daytime - use Sky addon
      this.sky.visible = true;
      this.skyDome.visible = false;
      this.starfield.visible = false;
    } else if (elevation > -12) {
      // Twilight - Sky fades out, stars fade in
      this.sky.visible = true;
      this.skyDome.visible = false;
      this.starfield.visible = true;
      this.starfield.material.opacity = Math.abs(elevation) / 12;
      this.starfield.material.transparent = true;
    } else {
      // Night - use skyDome gradient and starfield
      this.sky.visible = false;
      this.skyDome.visible = true;
      this.starfield.visible = true;
      this.starfield.material.opacity = 1;

      // Update night sky dome colors
      if (this.skyDome?.material?.uniforms) {
        this.skyDome.material.uniforms.uHorizonColor.value.setHex(params.skyHorizon);
        this.skyDome.material.uniforms.uZenithColor.value.setHex(params.skyZenith);
        this.skyDome.material.uniforms.uGroundColor.value.setHex(params.groundColor);
      }
    }

    // Update time inputs
    const h = Math.floor(solarHours);
    const m = Math.floor((solarHours % 1) * 60);
    if (this.timeHourInput && document.activeElement !== this.timeHourInput) {
      this.timeHourInput.value = h.toString().padStart(2, '0');
    }
    if (this.timeMinuteInput && document.activeElement !== this.timeMinuteInput) {
      this.timeMinuteInput.value = m.toString().padStart(2, '0');
    }
  }

  setLocation(latitude, longitude) {
    this.location = { latitude, longitude };
    this.isEarthBased = true;
    this.setResourceMode(true);
  }

  clearLocation() {
    this.isEarthBased = false;
    this.location = { latitude: 0, longitude: 0 };
  }

  setResourceMode(hasResource) {
    const showTimeControl = hasResource && this.isEarthBased;

    // Show/hide time control (left toolbar) - only for earth-based
    const toolbarLeft = this.container?.querySelector('.resource-toolbar-left');
    if (toolbarLeft) {
      toolbarLeft.style.display = showTimeControl ? '' : 'none';
    }

    // Show/hide bounds toggle (right toolbar) - for any resource
    const toolbar = this.container?.querySelector('.resource-toolbar');
    if (toolbar) {
      toolbar.style.display = hasResource ? '' : 'none';
    }

    // Toggle between sun lighting and starfield
    if (showTimeControl) {
      this.updateSunLighting();
    } else {
      // No resource or non-earth - show starfield night sky
      this.sky.visible = false;
      this.skyDome.visible = true;
      this.starfield.visible = true;
      this.starfield.material.opacity = 1;
      // Set night sky colors
      if (this.skyDome?.material?.uniforms) {
        this.skyDome.material.uniforms.uHorizonColor.value.setHex(0x0a0a15);
        this.skyDome.material.uniforms.uZenithColor.value.setHex(0x000010);
        this.skyDome.material.uniforms.uGroundColor.value.setHex(0x050508);
      }
      // Dim the key light
      if (this.keyLight) {
        this.keyLight.intensity = 0.3;
      }
    }
  }

  onClick(event) {
    if (this.videoPlanes.length === 0) return;

    const rect = this.renderer.domElement.getBoundingClientRect();
    this.mouse.x = ((event.clientX - rect.left) / rect.width) * 2 - 1;
    this.mouse.y = -((event.clientY - rect.top) / rect.height) * 2 + 1;

    this.raycaster.setFromCamera(this.mouse, this.camera);
    const intersects = this.raycaster.intersectObjects(this.videoPlanes, false);

    if (intersects.length > 0) {
      const mesh = intersects[0].object;
      const video = mesh.userData.video;
      if (video) {
        if (video.paused) {
          video.muted = false;
          video.play();
        } else {
          video.pause();
        }
      }
    }
  }

  onDoubleClick(event) {
    const rect = this.renderer.domElement.getBoundingClientRect();
    this.mouse.x = ((event.clientX - rect.left) / rect.width) * 2 - 1;
    this.mouse.y = -((event.clientY - rect.top) / rect.height) * 2 + 1;

    this.raycaster.setFromCamera(this.mouse, this.camera);

    const intersects = this.raycaster.intersectObjects(this.loadedModels, true);
    if (intersects.length > 0) {
      const hitObject = intersects[0].object;
      this.zoomToObject(hitObject);
    } else {
      this.fitCameraToContent();
    }
  }

  zoomToObject(object) {
    const boundingBox = new THREE.Box3().setFromObject(object);
    const center = new THREE.Vector3();
    const size = new THREE.Vector3();
    boundingBox.getCenter(center);
    boundingBox.getSize(size);

    const maxDim = Math.max(size.x, size.y, size.z);
    const fov = this.camera.fov * (Math.PI / 180);
    const distance = (maxDim / 2) / Math.tan(fov / 2) * 2;
    const minDistance = 2;
    const finalDistance = Math.max(distance, minDistance);

    const direction = this.camera.position.clone().sub(this.controls.target).normalize();
    const targetPosition = center.clone().add(direction.multiplyScalar(finalDistance));

    this.animateCamera(targetPosition, center);
  }

  animateCamera(targetPosition, targetLookAt) {
    // Cancel any existing camera animation
    if (this.cameraAnimationId) {
      cancelAnimationFrame(this.cameraAnimationId);
      this.cameraAnimationId = null;
    }

    const startPosition = this.camera.position.clone();
    const startLookAt = this.controls.target.clone();
    const duration = 500;
    const startTime = performance.now();

    const animate = (currentTime) => {
      if (this.disposed) return;

      const elapsed = currentTime - startTime;
      const t = Math.min(elapsed / duration, 1);
      const eased = t < 0.5 ? 4 * t * t * t : 1 - Math.pow(-2 * t + 2, 3) / 2;

      this.camera.position.lerpVectors(startPosition, targetPosition, eased);
      this.controls.target.lerpVectors(startLookAt, targetLookAt, eased);
      this.controls.update();

      if (t < 1) {
        this.cameraAnimationId = requestAnimationFrame(animate);
      } else {
        this.cameraAnimationId = null;
      }
    };

    this.cameraAnimationId = requestAnimationFrame(animate);
  }

  onWindowResize() {
    if (!this.container || !this.camera) return;
    if (!this._isVisible()) {
      this._suspendResourceLoads();
      return;
    }
    const wasHidden = this._wasHidden;
    this._wasHidden = false;
    const width = this.container.clientWidth;
    const height = this.container.clientHeight;
    if (width === 0 || height === 0) return;
    this.camera.aspect = width / height;
    this.camera.updateProjectionMatrix();
    this.renderer.setSize(width, height);
    if (wasHidden) {
      this.clock.getDelta(); // Exclude time spent in a background tab from resumed rotation.
      if (this.currentNode) this.setNode(this.currentNode);
    }
  }

  _isVisible() {
    return !!this.container?.offsetHeight && !!this.container?.offsetWidth && !document.hidden;
  }

  _isRequestCancelled(requestId) {
    return this.disposed || (requestId !== null && requestId !== this.loadRequestId);
  }

  _suspendResourceLoads() {
    clearTimeout(this._setNodeDebounce);
    if (this._wasHidden) return;
    this._wasHidden = true;
    ++this.loadRequestId;
    this.isLoading = false;
    // Retain completed models/caches; unfinished resources resume when shown.
    this.currentResourceUrl = null;
    if (this.cameraAnimationId) {
      cancelAnimationFrame(this.cameraAnimationId);
      this.cameraAnimationId = null;
      if (this.contentGroup) this.contentGroup.userData.needsCameraFit = true;
    }
  }

  animate() {
    if (this.disposed) return;

    this.animationFrameId = requestAnimationFrame(() => this.animate());

    const delta = this.clock.getDelta();
    if (!this._isVisible()) {
      this._suspendResourceLoads();
      return;
    }
    if (this._wasHidden) this.onWindowResize();
    this.updateRotators(delta);

    this.controls.update();
    this.sky.position.copy(this.camera.position);
    this.skyDome.position.copy(this.camera.position);
    if (this.starfield) {
      this.starfield.position.copy(this.camera.position);
    }

    this.renderer.render(this.scene, this.camera);
  }

  updateRotators(delta) {
    for (const rotator of this.rotators) {
      const { target, axis, speed } = rotator;
      if (target && target.parent) {
        const angle = speed * delta * (Math.PI / 180);  // Convert degrees/sec to radians
        target.rotateOnAxis(axis, angle);
      }
    }
  }

  _refreshIfSelected() {
    const selectedNode = this.model.getSelectedNode();
    if (selectedNode) {
      this.setNode(selectedNode);
    }
  }

  setNode(node) {
    this.currentNode = node;
    clearTimeout(this._setNodeDebounce);
    if (!this._isVisible()) {
      this._suspendResourceLoads();
      return;
    }
    this._setNodeDebounce = setTimeout(() => this._applySetNode(node), 150);
  }

  async _applySetNode(node) {
    if (node !== this.currentNode) return;
    if (!this._isVisible()) {
      this._suspendResourceLoads();
      return;
    }

    const resourceKeys = [];
    const resourceUrls = this._collectResourceUrls(node, resourceKeys);
    if (resourceUrls.length === 0) {
      this.clearScene();
      this.currentResourceUrl = null;
      this.setStatus('No resource', '');
      this.setResourceMode(false);
      return;
    }

    const nodeKey = this.model.nodeKey(node);
    // Include ownership, not just a URL multiset: two nodes may swap resources.
    const cacheKey = JSON.stringify([nodeKey, resourceKeys.sort()]);

    if (cacheKey === this.currentResourceUrl) {
      if (!this.isLoading) {
        const requestId = this.loadRequestId;
        await this._drawNode(node, this.contentGroup, requestId, true);
        if (this._isRequestCancelled(requestId) || node !== this.currentNode) return;
        this.applyWorldOrientation();
        this.updateBoundsDisplay();
      }
      return;
    }

    const isNewRoot = this.currentRootKey !== nodeKey;
    if (isNewRoot) {
      this.clearScene();
    }
    this.currentRootKey = nodeKey;
    this.currentResourceUrl = cacheKey;
    return this.loadNodeHierarchy(node, resourceUrls.length);
  }

  _collectResourceUrls(node, resourceKeys = null) {
    const urls = [];
    const collect = current => {
      if (current.resourceUrl) {
        urls.push(current.resourceUrl);
        resourceKeys?.push(JSON.stringify([this.model.nodeKey(current), current.resourceUrl,
          current.resourceActionType || null, current.resourceName || null]));
      }
      if (this.model.isNodeExpanded(current) && current.children) {
        for (const child of current.children) collect(child);
      }
    };
    collect(node);
    return urls;
  }

  async loadNodeHierarchy(rootNode, resourceCount) {
    const requestId = ++this.loadRequestId;
    this.isLoading = true;

    const isFirstLoad = !this.contentGroup;

    if (isFirstLoad) {
      this.contentGroup = new THREE.Group();
      this.contentGroup.userData.needsCameraFit = true;
      this.scene.add(this.contentGroup);
    }

    this._precomputeScale(rootNode);
    this.setStatus(`Loading ${resourceCount} resource(s)...`, 'loading');

    const isCancelled = () => requestId !== this.loadRequestId;

    // Collect expected node keys before drawing
    const expectedKeys = new Set();
    this._collectVisibleNodeKeys(rootNode, expectedKeys);

    // Remove stale nodes
    this._removeStaleNodes(expectedKeys);

    try {
      await this._drawNode(rootNode, this.contentGroup, requestId, true);

      if (isCancelled()) {
        return;
      }

      this.centerContentAtOrigin();
      if (this.contentGroup.userData.needsCameraFit) {
        this.fitCameraToContent();
        this.animateCameraToContent();
        this.contentGroup.userData.needsCameraFit = false;
      }
      this.applyWorldOrientation();
      this.updateGridFromContent();
      this.updateBoundsDisplay();
      this.setStatus('', '');
      this.setResourceMode(true);
    } catch (error) {
      if (requestId === this.loadRequestId) {
        this.setStatus(`Failed: ${error.message}`, 'error');
        console.error('Resource load error:', error);
      }
    } finally {
      if (requestId === this.loadRequestId) {
        this.isLoading = false;
      }
    }
  }

  _collectVisibleNodeKeys(node, keys) {
    const nodeKey = this.model.nodeKey(node);
    keys.add(nodeKey);
    if (this.model.isNodeExpanded(node) && node.children) {
      for (const child of node.children) {
        this._collectVisibleNodeKeys(child, keys);
      }
    }
  }

  _removeStaleNodes(expectedKeys) {
    const staleKeys = [];
    for (const [key, group] of this.nodeGroups) {
      if (!expectedKeys.has(key)) {
        staleKeys.push(key);
        this._removeNodeResource(key, false);
        if (group) {
          // Dispose geometry/materials and remove from scene
          this._disposeLoadedObject(group, true);
          group.parent?.remove(group);
        }
      }
    }
    for (const key of staleKeys) {
      this.nodeGroups.delete(key);
    }

    // Remove stale entries from loadedModels
    this.loadedModels = this.loadedModels.filter(model => this._isInGroup(model, this.contentGroup));
  }

  _isInGroup(object, group) {
    for (let current = object; current; current = current.parent) {
      if (current === group) return true;
    }
    return false;
  }

  _removeNodeResource(nodeKey, pruneModels = true) {
    const group = this.nodeResourceGroups.get(nodeKey);
    if (group) {
      if (pruneModels) this.loadedModels = this.loadedModels.filter(model => !this._isInGroup(model, group));
      this._disposeLoadedObject(group, true);
      group.parent?.remove(group);
    }
    this.nodeResourceGroups.delete(nodeKey);
    this.rotators = this.rotators.filter(rotator => rotator.sourceNodeKey !== nodeKey);
  }

  _applyNodeTransformToGroup(group, transform) {
    const pos = transform.position || { x: 0, y: 0, z: 0 };
    const rot = transform.rotation || { x: 0, y: 0, z: 0, w: 1 };
    const scl = transform.scale || { x: 1, y: 1, z: 1 };
    group.position.set(pos.x, pos.y, pos.z);
    group.quaternion.set(rot.x, rot.y, rot.z, rot.w);
    group.scale.set(scl.x, scl.y, scl.z);
  }

  _getScopeBaseUrl(node = this.currentNode) {
    return NodeAdapter.getScopeResourceRoot(node?.fabricScopeId || null);
  }

  async _drawNode(node, parentGroup, requestId, isRoot = false) {
    if (this._isRequestCancelled(requestId)) return;

    const nodeKey = this.model.nodeKey(node);
    const isNew = !this.nodeGroups.has(nodeKey);

    const hasResource = !!node.resourceUrl;
    const expandedChildren = this.model.isNodeExpanded(node) && node.children;
    const hasTransform = !isRoot && node.transform;
    const needsGroup = hasTransform && (hasResource || expandedChildren);

    let target = parentGroup;

    if (needsGroup) {
      let group = this.nodeGroups.get(nodeKey);
      if (!group) {
        group = new THREE.Group();
        group.name = node.name || 'node';
        parentGroup.add(group);
        this.nodeGroups.set(nodeKey, group);
      } else if (group.parent !== parentGroup) {
        group.parent.remove(group);
        parentGroup.add(group);
      }
      this._applyNodeTransformToGroup(group, node.transform);
      target = group;
    } else if (isNew) {
      this.nodeGroups.set(nodeKey, null);
    }

    const resourceKey = JSON.stringify([node.resourceUrl || null, node.resourceActionType || null, node.resourceName || null]);
    const previousResource = this.nodeResourceGroups.get(nodeKey)?.userData.resourceState;
    const needsResource = hasResource && (!previousResource || previousResource.key !== resourceKey ||
      (!previousResource.complete && previousResource.requestId !== requestId));
    if ((!hasResource && previousResource) || needsResource) this._removeNodeResource(nodeKey);

    if (needsResource) {
      const resourceState = { key: resourceKey, requestId, complete: false };
      const resourceGroup = new THREE.Group();
      resourceGroup.userData.resourceState = resourceState;
      resourceGroup.visible = !node.isHiddenInResource;
      target.add(resourceGroup);
      this.nodeResourceGroups.set(nodeKey, resourceGroup);
      let loaded = false;
      const actionType = node.resourceActionType;
      if (actionType === 'rotator' && node.resourceName) {
        loaded = await this.setupRotator({ resourceName: node.resourceName }, parentGroup, requestId, nodeKey);
      } else {
        const nodeTransform = (hasTransform && !needsGroup) ? node.transform : null;
        const lower = node.resourceUrl.toLowerCase();
        if (lower.endsWith('.glb') || lower.endsWith('.gltf')) {
          loaded = await this.loadDirectGlb(node.resourceUrl, nodeTransform, requestId, resourceGroup);
        } else {
          loaded = await this.loadResourceWithTransform(node.resourceUrl, nodeTransform, requestId, resourceGroup);
        }
      }
      if (this._isRequestCancelled(requestId)) return;
      resourceState.complete = loaded === true;
    }

    if (expandedChildren) {
      await Promise.all(node.children.map(child =>
        this._drawNode(child, target, requestId)
      ));
    }
  }

  async _acquireFetchSlot() {
    const sem = this._fetchSemaphore;
    if (sem.active < sem.limit) {
      sem.active++;
      return;
    }
    await new Promise(resolve => sem.queue.push(resolve));
  }

  _releaseFetchSlot() {
    const sem = this._fetchSemaphore;
    if (sem.queue.length > 0) {
      sem.queue.shift()();
    } else {
      sem.active--;
    }
  }

  async _throttledFetch(url, requestId) {
    if (this._isRequestCancelled(requestId)) return null;
    await this._acquireFetchSlot();
    try {
      if (this._isRequestCancelled(requestId)) return null;
      return await fetch(url);
    } finally {
      this._releaseFetchSlot();
    }
  }

  cleanupCancelledLoad() {
    if (this.contentGroup) {
      this.scene.remove(this.contentGroup);
      this.contentGroup = null;
    }
    this.loadedModels = [];
    this.nodeGroups.clear();
    this.nodeResourceGroups.clear();
    this.rotators = [];

    // Clean up video elements from cancelled load
    for (const mesh of [...this.videoPlanes]) this._releaseVideoPlane(mesh);

    for (const hls of this.hlsInstances) {
      hls.destroy();
    }
    this.hlsInstances = [];
  }

  async loadDirectGlb(url, nodeTransform, requestId, targetGroup = null) {
    // Return true only after attachment; cancellation and load errors return false.
    if (this._isRequestCancelled(requestId)) return false;
    await this._acquireFetchSlot();
    try {
      if (this._isRequestCancelled(requestId)) return false;
      return await new Promise((resolve) => {
        this.gltfLoader.load(
          url,
          (gltf) => {
            const group = targetGroup || this.contentGroup;
            if (this._isRequestCancelled(requestId) || !group) {
              this._disposeLoadedObject(gltf.scene);
              resolve(false);
              return;
            }
            const model = gltf.scene;
            this.setupModelMaterials(model);
            if (nodeTransform) {
              this.applyNodeTransform(model, nodeTransform);
            }
            group.add(model);
            this.loadedModels.push(model);
            if (this.loadedModels.length === 1) {
              this.centerContentAtOrigin();
            }
            resolve(true);
          },
          undefined,
          (error) => {
            console.warn(`Failed to load GLB ${url}:`, error);
            resolve(false);
          }
        );
      });
    } finally {
      this._releaseFetchSlot();
    }
  }

  async loadActionResource(resourceRef, resourceName, nodeTransform, requestId, targetGroup = null) {
    const group = targetGroup || this.contentGroup;
    const actionType = resourceRef?.replace('action://', '').replace(/\.json$/, '');

    if (actionType === 'rotator' && resourceName) {
      await this.setupRotator({ resourceName }, group, requestId);
      return;
    }

    const transformMatrix = new THREE.Matrix4();
    if (nodeTransform) {
      const pos = nodeTransform.Position || nodeTransform.position || [0, 0, 0];
      const rot = nodeTransform.Rotation || nodeTransform.rotation || [0, 0, 0, 1];
      const scale = nodeTransform.Scale || nodeTransform.scale || [1, 1, 1];
      transformMatrix.compose(
        new THREE.Vector3(pos[0], pos[1], pos[2]),
        new THREE.Quaternion(rot[0] ?? 0, rot[1] ?? 0, rot[2] ?? 0, rot[3] ?? 1),
        new THREE.Vector3(scale[0] ?? 1, scale[1] ?? 1, scale[2] ?? 1)
      );
    }

    const obj = {
      resourceReference: resourceRef,
      resourceName: resourceName,
      objectBounds: null,
      transform: transformMatrix
    };

    const model = await this.loadPhysicalObject(obj, requestId);
    if (model && group && (requestId === null || requestId === this.loadRequestId)) {
      this.setupModelMaterials(model);
      group.add(model);
      this.loadedModels.push(model);
    }
  }

  async loadResourceWithTransform(url, nodeTransform, requestId, targetGroup = null) {
    // Propagate attachment success so a failed retained resource can retry.
    try {
      const response = await this._throttledFetch(url, requestId);
      if (!response) return false;
      if (!response.ok) {
        console.warn(`Failed to fetch ${url}: HTTP ${response.status}`);
        return false;
      }

      if (this._isRequestCancelled(requestId)) return false;

      const contentType = response.headers.get('content-type') || '';
      if (!contentType.includes('json')) {
        return false;
      }

      const baseDir = url.substring(0, url.lastIndexOf('/') + 1);
      const data = await response.json();
      return await this.processResourceData(data, nodeTransform, requestId, baseDir, targetGroup);
    } catch (error) {
      console.warn(`Error loading resource ${url}:`, error);
      return false;
    }
  }

  async loadResourceJson(url) {
    if (this.isLoading) return;
    this.isLoading = true;

    // Default to Earth at 0/0 for direct URL loads
    if (!this.isEarthBased) {
      this.setLocation(0, 0);
    }

    this.clearScene();
    this.isLoading = true;
    this.contentGroup = new THREE.Group();
    this.scene.add(this.contentGroup);
    this.setStatus('Loading resource...', 'loading');

    const requestId = ++this.loadRequestId;

    try {
      const response = await fetch(url);
      if (!response.ok) {
        throw new Error(`HTTP ${response.status}`);
      }

      if (requestId !== this.loadRequestId) return;

      const baseDir = url.substring(0, url.lastIndexOf('/') + 1);
      const data = await response.json();
      await this.processResourceData(data, null, requestId, baseDir);

      if (requestId !== this.loadRequestId) return;

      this.fitCameraToContent();
      this.updateGridFromContent();
      this.setStatus('', '');
    } catch (error) {
      if (requestId === this.loadRequestId) {
        this.setStatus(`Failed: ${error.message}`, 'error');
        console.error('Resource load error:', error);
      }
    } finally {
      if (requestId === this.loadRequestId) {
        this.isLoading = false;
      }
    }
  }

  async processResourceData(data, nodeTransform = null, requestId = null, baseDir = null, targetGroup = null) {
    if (this._isRequestCancelled(requestId)) return false;
    const group = targetGroup || this.contentGroup;

    // Handle metadata files (have lods, no blueprint)
    const lods = data?.lods || data?.LODs;
    if (lods && lods.length > 0) {
      const lod0 = lods[0];
      const glbName = typeof lod0 === 'string' ? lod0 : lod0._url || lod0.file || lod0.name;
      if (glbName) {
        const glbUrl = glbName.startsWith('http') ? glbName : (baseDir ? baseDir + glbName : glbName);
        const model = await this.loadGlb(glbUrl, requestId);
        if (model && group && (requestId === null || requestId === this.loadRequestId)) {
          this.setupModelMaterials(model);
          if (nodeTransform) {
            this.applyNodeTransform(model, nodeTransform);
          }
          group.add(model);
          this.loadedModels.push(model);
          if (this.loadedModels.length === 1) {
            this.centerContentAtOrigin();
          }
          return true;
        }
      }
      return false;
    }

    // Handle scene files (have body.blueprint)
    const blueprint = data?.body?.blueprint;
    if (!blueprint) {
      return false;
    }

    if (!this.isEarthBased && this.isSimplePhysicalBlueprint(blueprint)) {
      this.setLocation(0, 0);
    }

    if (this._isRequestCancelled(requestId)) return false;

    const result = await this.processBlueprintNode(blueprint, requestId);
    if (result && group && (requestId === null || requestId === this.loadRequestId)) {
      this.setupModelMaterials(result);
      if (nodeTransform) {
        this.applyNodeTransform(result, nodeTransform);
      }
      group.add(result);
      this.loadedModels.push(result);
      if (this.loadedModels.length === 1) {
        this.centerContentAtOrigin();
      }
      return !result.userData.resourceLoadFailed;
    }
    if (result) this._disposeLoadedObject(result, true);
    return false;
  }

  async processBlueprintNode(node, requestId = null) {
    // Check if request is still current
    if (this._isRequestCancelled(requestId)) return null;

    const hasChildren = node.children && Array.isArray(node.children) && node.children.length > 0;
    const isPhysical = node.blueprintType === 'physical' && node.resourceReference;

    // Create transform for this node
    const pos = node.pos || [0, 0, 0];
    const rot = node.rot || [0, 0, 0, 1];
    const scale = node.scale || [1, 1, 1];

    // For nodes with children, create a THREE.Group
    if (hasChildren) {
      const group = new THREE.Group();
      group.name = node.name || 'group';

      let retained = false;
      try {
        // Apply local transform to group
        group.position.set(pos[0], pos[1], pos[2]);
        group.quaternion.set(rot[0], rot[1], rot[2], rot[3]);
        group.scale.set(scale[0], scale[1], scale[2]);

        // If this node also has a resource (e.g., sign with text children), load it first
        if (isPhysical) {
          const obj = {
            resourceReference: node.resourceReference,
            resourceName: node.resourceName,
            objectBounds: node.objectBounds,
            transform: new THREE.Matrix4()
          };
          const nodeModel = await this.loadPhysicalObject(obj, requestId);
          if (nodeModel) {
            group.add(nodeModel);
          } else {
            group.userData.resourceLoadFailed = true;
          }
        }

        // Process children and collect any rotators
        const pendingRotators = [];
        for (const child of node.children) {
          // Check if request is still current before each child
          if (requestId !== null && requestId !== this.loadRequestId) return null;

          // Check for rotator - handle specially
          const childActionType = child.resourceReference?.startsWith('action://')
            ? child.resourceReference.split('/').pop().replace(/\.json$/, '')
            : null;
          if (childActionType === 'rotator') {
            pendingRotators.push(child);
            continue;
          }

          const childResult = await this.processBlueprintNode(child, requestId);
          if (childResult) {
            group.add(childResult);
            if (childResult.userData.resourceLoadFailed) group.userData.resourceLoadFailed = true;
          } else if (child.blueprintType === 'physical' && child.resourceReference) {
            group.userData.resourceLoadFailed = true;
          }
        }

        // Set up rotators to target this group
        for (const rotatorNode of pendingRotators) {
          if (requestId !== null && requestId !== this.loadRequestId) return null;
          if (!await this.setupRotator(rotatorNode, group, requestId)) group.userData.resourceLoadFailed = true;
        }

        if (this._isRequestCancelled(requestId)) return null;
        retained = group.children.length > 0 || pendingRotators.length > 0;
        return retained ? group : null;
      } finally {
        if (!retained) this._disposeLoadedObject(group, true);
      }
    }

    // For leaf physical nodes, load the resource
    if (isPhysical) {
      const obj = {
        resourceReference: node.resourceReference,
        resourceName: node.resourceName,
        objectBounds: node.objectBounds,
        transform: new THREE.Matrix4()  // Identity - transform applied separately
      };

      const model = await this.loadPhysicalObject(obj, requestId);
      if (this._isRequestCancelled(requestId)) {
        if (model) this._disposeLoadedObject(model, true);
        return null;
      }
      if (model) {
        // Apply local transform
        model.position.set(pos[0], pos[1], pos[2]);
        model.quaternion.set(rot[0], rot[1], rot[2], rot[3]);
        model.scale.set(scale[0], scale[1], scale[2]);
      }
      return model;
    }

    return null;
  }

  isSimplePhysicalBlueprint(blueprint) {
    if (!blueprint) return false;

    const isPhysical = blueprint.blueprintType === 'physical' && blueprint.resourceReference;
    if (isPhysical) return true;

    if (blueprint.children && blueprint.children.length > 0) {
      const hasOnlyPhysicalChildren = blueprint.children.every(child =>
        child.blueprintType === 'physical' ||
        child.resourceReference?.startsWith('action://')
      );
      if (hasOnlyPhysicalChildren) return true;
    }

    return false;
  }

  async fetchResourceJson(resourceName, requestId = null, scopeBaseUrl = null) {
    if (!resourceName) return null;
    const url = resolveResourceUrl(resourceName, scopeBaseUrl);
    if (!url) {
      console.warn('fetchResourceJson: Could not resolve URL for', resourceName);
      return null;
    }

    try {
      const response = await this._throttledFetch(url, requestId);
      if (!response) return null;
      if (!response.ok) {
        console.warn(`fetchResourceJson: HTTP ${response.status} for ${url}`);
        return null;
      }
      return await response.json();
    } catch (e) {
      console.error('fetchResourceJson: Failed to fetch', url, e);
      return null;
    }
  }

  async setupRotator(rotatorNode, targetGroup, requestId = null, sourceNodeKey = null) {
    const data = await this.fetchResourceJson(rotatorNode.resourceName, requestId, this._getScopeBaseUrl());
    if (!data || this._isRequestCancelled(requestId)) return false;

    const parentLevels = data?.body?.parent || 0;
    let target = targetGroup;
    for (let i = 0; i < parentLevels && target.parent; i++) {
      target = target.parent;
    }

    const axisArray = data?.body?.axis || [0, 1, 0];
    const speed = data?.body?.rotSpeed || 10;

    this.rotators.push({
      sourceNodeKey,
      target: target,
      axis: new THREE.Vector3(axisArray[0], axisArray[1], axisArray[2]).normalize(),
      speed: speed
    });
    return true;
  }

  setupModelMaterials(model) {
    model.traverse((child) => {
      if (child.isMesh) {
        child.castShadow = true;
        child.receiveShadow = true;

        // Ensure materials work with environment lighting
        if (child.material) {
          const materials = Array.isArray(child.material) ? child.material : [child.material];
          for (const mat of materials) {
            if (mat.isMeshStandardMaterial || mat.isMeshPhysicalMaterial) {
              mat.envMapIntensity = 1.0;
            }
          }
        }
      }
    });
  }

  applyNodeTransform(model, transform) {
    if (!transform) return;

    const pos = transform.Position || transform.position || [0, 0, 0];
    const rot = transform.Rotation || transform.rotation || [0, 0, 0, 1];
    const scale = transform.Scale || transform.scale || [1, 1, 1];

    if (!isFinite(pos[0]) || !isFinite(pos[1]) || !isFinite(pos[2])) {
      return;
    }

    const offsetMatrix = new THREE.Matrix4();
    const position = new THREE.Vector3(pos[0], pos[1], pos[2]);
    const quaternion = new THREE.Quaternion(rot[0] ?? 0, rot[1] ?? 0, rot[2] ?? 0, rot[3] ?? 1);
    const scaleVec = new THREE.Vector3(scale[0] ?? 1, scale[1] ?? 1, scale[2] ?? 1);

    offsetMatrix.compose(position, quaternion, scaleVec);
    model.applyMatrix4(offsetMatrix);
  }

  async loadPhysicalObject(obj, requestId = null, scopeBaseUrl = this._getScopeBaseUrl()) {
    const { resourceReference, resourceName, objectBounds, transform } = obj;

    // Extract action type from action:// references (strip .json if present)
    let actionType = null;
    if (resourceReference?.startsWith('action://')) {
      actionType = resourceReference.split('/').pop().replace(/\.json$/, '');
    }

    // Handle point lights
    if (actionType === 'pointlight') {
      return this.loadPointLight(resourceName, transform, requestId, scopeBaseUrl);
    }

    // Handle text sprites
    if (actionType === 'showtext') {
      return this.loadTextSprite(resourceName, transform, objectBounds, requestId, scopeBaseUrl);
    }

    // Handle video planes
    if (actionType === 'video') {
      return this.loadVideoPlane(resourceName, transform, objectBounds, requestId, scopeBaseUrl);
    }

    // Skip non-visual action types (behavioral, UI, sync components)
    const nonVisualActions = [
      'collider',
      'interior',
      'testswitch',
      'player_controller',
      'activity',
      'modeshow',
      'textinput',
      'demolight',
      'textsync',
      'button',
      'entitysync',
      'widgetframe',
      'actioncon',
      'motor',
      'rotator',
    ];
    if (actionType && nonVisualActions.includes(actionType)) {
      return null;
    }

    // Handle nested scenes (action://scene means load resourceName as another scene)
    if (actionType === 'scene' && resourceName) {
      return this.loadNestedScene(resourceName, transform, requestId, scopeBaseUrl);
    }

    // Handle direct GLB/GLTF references
    const refLower = resourceReference.toLowerCase();
    if (refLower.endsWith('.glb') || refLower.endsWith('.gltf')) {
      const model = await this.loadGlb(resourceReference, requestId);
      if (model) {
        model.applyMatrix4(transform);
      }
      return model;
    }

    // Handle metadata files with LODs
    const { metadata, baseDir } = await this.loadMetadata(resourceReference, scopeBaseUrl, requestId);
    const lods = metadata?.lods || metadata?.LODs;
    if (!metadata || !lods || lods.length === 0) {
      console.warn(`No LODs in metadata: ${resourceReference}`);
      return null;
    }

    const lod0 = lods[0];
    const glbName = typeof lod0 === 'string' ? lod0 : lod0._url || lod0.file || lod0.name;

    if (!glbName) {
      console.warn(`No GLB filename in metadata: ${resourceReference}`);
      return null;
    }

    const glbUrl = glbName.startsWith('http') ? glbName : (baseDir ? baseDir + glbName : glbName);
    const model = await this.loadGlb(glbUrl, requestId, scopeBaseUrl);
    if (!model) {
      return null;
    }

    model.applyMatrix4(transform);

    return model;
  }

  async loadNestedScene(sceneName, parentTransform, requestId = null, scopeBaseUrl = this._getScopeBaseUrl()) {
    try {
      const url = resolveResourceUrl('action://' + sceneName, scopeBaseUrl);
      const response = await this._throttledFetch(url, requestId);
      if (!response) return null;
      if (!response.ok) {
        console.warn(`Failed to load nested scene: ${sceneName}`);
        return null;
      }

      if (requestId !== null && requestId !== this.loadRequestId) return null;

      const data = await response.json();
      const blueprint = data?.body?.blueprint;
      if (!blueprint) {
        console.warn(`No blueprint in nested scene: ${sceneName}`);
        return null;
      }

      return this.processBlueprintNode(blueprint, requestId);
    } catch (error) {
      console.warn(`Error loading nested scene ${sceneName}:`, error);
      return null;
    }
  }

  async loadTextSprite(resourceName, transform, objectBounds, requestId = null, scopeBaseUrl = this._getScopeBaseUrl()) {
    const data = await this.fetchResourceJson(resourceName, requestId, scopeBaseUrl);
    if (this._isRequestCancelled(requestId)) return null;
    const text = data?.body?.text || 'Text';

    // Create canvas for text
    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');
    const fontSize = 64;
    ctx.font = `bold ${fontSize}px Arial`;

    // Measure text and size canvas
    const metrics = ctx.measureText(text);
    const textWidth = metrics.width;
    const textHeight = fontSize * 1.2;

    canvas.width = Math.ceil(textWidth) + 20;
    canvas.height = Math.ceil(textHeight) + 20;

    // Redraw with correct canvas size
    ctx.font = `bold ${fontSize}px Arial`;
    ctx.fillStyle = 'white';
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';
    ctx.fillText(text, canvas.width / 2, canvas.height / 2);

    // Create plane mesh (respects rotation unlike sprites)
    const texture = new THREE.CanvasTexture(canvas);
    texture.needsUpdate = true;

    const material = new THREE.MeshBasicMaterial({
      map: texture,
      transparent: true,
      side: THREE.FrontSide,
      depthWrite: false
    });

    // Create 1-unit wide plane (aspect ratio preserved), scale handles final size
    const aspectRatio = canvas.height / canvas.width;
    const planeWidth = 1;
    const planeHeight = aspectRatio;

    const geometry = new THREE.PlaneGeometry(planeWidth, planeHeight);
    const mesh = new THREE.Mesh(geometry, material);

    // Apply transform
    mesh.applyMatrix4(transform);

    return mesh;
  }

  async loadPointLight(resourceName, transform, requestId = null, scopeBaseUrl = this._getScopeBaseUrl()) {
    let color = new THREE.Color(1, 0.9, 0.8);
    let intensity = 1;
    let distance = 100;

    const data = await this.fetchResourceJson(resourceName, requestId, scopeBaseUrl);
    if (this._isRequestCancelled(requestId)) return null;
    const colorArray = data?.body?.color;
    if (colorArray && colorArray.length >= 3) {
      color = new THREE.Color(colorArray[0], colorArray[1], colorArray[2]);
      if (colorArray.length >= 4) {
        distance = colorArray[3];
        intensity = Math.min(colorArray[3] / 100, 10);
      }
    }

    // Create point light
    const light = new THREE.PointLight(color, intensity, distance);

    // Apply transform
    light.applyMatrix4(transform);

    return light;
  }

  async loadVideoPlane(resourceName, transform, objectBounds, requestId = null, scopeBaseUrl = this._getScopeBaseUrl()) {
    const data = await this.fetchResourceJson(resourceName, requestId, scopeBaseUrl);
    if (this._isRequestCancelled(requestId)) return null;
    const sources = data?.body?.streamConfig?.sources;
    const videoUrl = sources?.[0];

    if (!videoUrl) {
      return null;
    }

    // Create video element
    const video = document.createElement('video');
    video.crossOrigin = 'anonymous';
    video.loop = true;
    video.muted = true;
    video.playsInline = true;

    // Handle HLS streams
    const isHls = videoUrl.toLowerCase().includes('.m3u8');
    let hlsController = null;
    if (isHls && Hls.isSupported()) {
      const hls = new Hls();
      hlsController = hls;
      hls.on(Hls.Events.ERROR, (event, data) => {
        console.error('HLS error:', data.type, data.details);
      });
      hls.loadSource(videoUrl);
      hls.attachMedia(video);
      this.hlsInstances.push(hls);
    } else if (video.canPlayType('application/vnd.apple.mpegurl')) {
      // Safari native HLS support
      video.src = videoUrl;
    } else {
      video.src = videoUrl;
    }

    // Create video texture
    const texture = new THREE.VideoTexture(video);
    texture.minFilter = THREE.LinearFilter;
    texture.magFilter = THREE.LinearFilter;
    texture.colorSpace = THREE.SRGBColorSpace;

    const material = new THREE.MeshBasicMaterial({
      map: texture,
      side: THREE.DoubleSide
    });

    // Start with 1x1, update to video aspect when metadata loads
    const geometry = new THREE.PlaneGeometry(1, 1);
    const mesh = new THREE.Mesh(geometry, material);
    mesh.userData.video = video;
    mesh.userData.hls = hlsController;
    mesh.userData.isVideoPlane = true;

    let geometryUpdated = false;
    const updateGeometryFromVideo = () => {
      if (!mesh.userData.video) return true;
      if (geometryUpdated) return true;
      if (video.videoWidth && video.videoHeight) {
        const aspect = video.videoWidth / video.videoHeight;
        mesh.geometry.dispose();
        mesh.geometry = new THREE.PlaneGeometry(aspect, 1);
        geometryUpdated = true;
        return true;
      }
      return false;
    };

    // Check if metadata already loaded, otherwise listen for multiple events
    // (Safari's native HLS may fire loadedmetadata with 0x0, but loadeddata has dimensions)
    if (!updateGeometryFromVideo()) {
      video.addEventListener('loadedmetadata', updateGeometryFromVideo, { once: true });
      video.addEventListener('loadeddata', updateGeometryFromVideo, { once: true });
      video.addEventListener('error', (e) => {
        console.error('Video error:', e, video.error);
      });
    }
    mesh.userData.releaseVideoListeners = () => {
      video.removeEventListener('loadedmetadata', updateGeometryFromVideo);
      video.removeEventListener('loadeddata', updateGeometryFromVideo);
    };


    // Apply transform
    mesh.applyMatrix4(transform);

    // Track for click handling
    this.videoPlanes.push(mesh);

    return mesh;
  }

  async loadMetadata(metadataRef, scopeBaseUrl = this._getScopeBaseUrl(), requestId = null) {
    if (this._isRequestCancelled(requestId)) return { metadata: null, baseDir: null };
    const url = resolveResourceUrl(metadataRef, scopeBaseUrl);
    if (!url) return { metadata: null, baseDir: null };

    if (this.metadataCache.has(url)) {
      const baseDir = url.substring(0, url.lastIndexOf('/') + 1);
      return { metadata: this.metadataCache.get(url), baseDir };
    }

    await this._acquireFetchSlot();
    try {
      if (this._isRequestCancelled(requestId)) return { metadata: null, baseDir: null };
      const response = await fetch(url);
      if (!response.ok) {
        console.warn(`Metadata not found: ${metadataRef}`);
        return { metadata: null, baseDir: null };
      }
      const metadata = await response.json();
      if (this._isRequestCancelled(requestId)) return { metadata: null, baseDir: null };
      this.metadataCache.set(url, metadata);
      const baseDir = url.substring(0, url.lastIndexOf('/') + 1);
      return { metadata, baseDir };
    } catch (error) {
      console.warn(`Failed to load metadata ${metadataRef}:`, error);
      return { metadata: null, baseDir: null };
    } finally {
      this._releaseFetchSlot();
    }
  }

  async loadGlb(glbName, requestId = null, scopeBaseUrl = this._getScopeBaseUrl()) {
    if (this._isRequestCancelled(requestId)) return null;
    const url = resolveResourceUrl(glbName, scopeBaseUrl);

    if (this.glbCache.has(url)) {
      return this.glbCache.get(url).clone();
    }

    await this._acquireFetchSlot();
    try {
      if (this._isRequestCancelled(requestId)) return null;
      return await new Promise((resolve) => {
        this.gltfLoader.load(
          url,
          (gltf) => {
            if (this._isRequestCancelled(requestId)) {
              this._disposeLoadedObject(gltf.scene);
              resolve(null);
              return;
            }
            this.glbCache.set(url, gltf.scene);
            // Three.js clones share these assets; the cached template owns disposal.
            gltf.scene.traverse(child => {
              if (child.geometry) child.geometry[cachedAssetOwner] = this;
              if (child.material) {
                const materials = Array.isArray(child.material) ? child.material : [child.material];
                materials.forEach(material => { material[cachedAssetOwner] = this; });
              }
            });
            resolve(gltf.scene.clone());
          },
          undefined,
          (error) => {
            console.warn(`Failed to load GLB ${glbName}:`, error);
            resolve(null);
          }
      );
      });
    } finally {
      this._releaseFetchSlot();
    }
  }

  _nodeTransformToMatrix(transform) {
    const pos = transform.position || { x: 0, y: 0, z: 0 };
    const rot = transform.rotation || { x: 0, y: 0, z: 0, w: 1 };
    const scl = transform.scale || { x: 1, y: 1, z: 1 };
    return new THREE.Matrix4().compose(
      new THREE.Vector3(pos.x, pos.y, pos.z),
      new THREE.Quaternion(rot.x, rot.y, rot.z, rot.w),
      new THREE.Vector3(scl.x, scl.y, scl.z)
    );
  }

  // Like _nodeTransformToMatrix, but with identity scale. The node's own bound
  // is in world units already (per fabric convention), so we must not multiply
  // it by the node's own scale when computing world-space bound corners — that
  // would double-apply the scale. Children still use the scaled matrix when
  // they descend, so child positions inherit parent scale as expected.
  _nodeTransformToBoundMatrix(transform) {
    const pos = transform.position || { x: 0, y: 0, z: 0 };
    const rot = transform.rotation || { x: 0, y: 0, z: 0, w: 1 };
    return new THREE.Matrix4().compose(
      new THREE.Vector3(pos.x, pos.y, pos.z),
      new THREE.Quaternion(rot.x, rot.y, rot.z, rot.w),
      new THREE.Vector3(1, 1, 1)
    );
  }

  _computeNodeBounds(node, parentMatrix, isRoot = false) {
    const localBoundMatrix = (!isRoot && node.transform)
      ? this._nodeTransformToBoundMatrix(node.transform)
      : new THREE.Matrix4();
    const localChildMatrix = (!isRoot && node.transform)
      ? this._nodeTransformToMatrix(node.transform)
      : new THREE.Matrix4();

    const worldBoundMatrix = new THREE.Matrix4().multiplyMatrices(parentMatrix, localBoundMatrix);
    const worldChildMatrix = new THREE.Matrix4().multiplyMatrices(parentMatrix, localChildMatrix);
    const bounds = new THREE.Box3();

    if (node.bound && node.bound.x != null && node.bound.y != null && node.bound.z != null) {
      const localBox = new THREE.Box3(
        new THREE.Vector3(-node.bound.x, 0, -node.bound.z),
        new THREE.Vector3(node.bound.x, node.bound.y, node.bound.z)
      );
      const corners = [
        new THREE.Vector3(localBox.min.x, localBox.min.y, localBox.min.z),
        new THREE.Vector3(localBox.min.x, localBox.min.y, localBox.max.z),
        new THREE.Vector3(localBox.min.x, localBox.max.y, localBox.min.z),
        new THREE.Vector3(localBox.min.x, localBox.max.y, localBox.max.z),
        new THREE.Vector3(localBox.max.x, localBox.min.y, localBox.min.z),
        new THREE.Vector3(localBox.max.x, localBox.min.y, localBox.max.z),
        new THREE.Vector3(localBox.max.x, localBox.max.y, localBox.min.z),
        new THREE.Vector3(localBox.max.x, localBox.max.y, localBox.max.z),
      ];
      for (const corner of corners) {
        corner.applyMatrix4(worldBoundMatrix);
        bounds.expandByPoint(corner);
      }
    } else if (!isRoot && node.transform) {
      const pos = new THREE.Vector3().setFromMatrixPosition(worldChildMatrix);
      bounds.expandByPoint(pos);
    }

    if (this.model.isNodeExpanded(node) && node.children) {
      for (const child of node.children) {
        const childBounds = this._computeNodeBounds(child, worldChildMatrix);
        if (!childBounds.isEmpty()) {
          bounds.union(childBounds);
        }
      }
    }

    return bounds;
  }

  _precomputeScale(rootNode) {
    const identity = new THREE.Matrix4();
    const bounds = this._computeNodeBounds(rootNode, identity, true);

    if (bounds.isEmpty() || !isFinite(bounds.min.x) || !isFinite(bounds.max.x)) {
      this.resetCamera();
      return;
    }

    const size = new THREE.Vector3();
    const center = new THREE.Vector3();
    bounds.getSize(size);
    bounds.getCenter(center);

    const maxDim = Math.max(size.x, size.y, size.z);
    if (maxDim <= 0) {
      this.resetCamera();
      return;
    }

    const targetSize = 1500;
    const minScale = 0.0001;
    const maxScale = 10000;
    let scale = targetSize / maxDim;
    scale = Math.max(minScale, Math.min(maxScale, scale));

    this.contentGroup.scale.setScalar(scale);
    this._precomputedScale = true;

    const scaledSize = size.clone().multiplyScalar(scale);
    const finalDistance = this._cameraDistanceForSize(scaledSize);
    const scaledCenter = center.clone().multiplyScalar(scale);
    const direction = new THREE.Vector3(1, 0.5, 1).normalize();
    this.camera.position.copy(scaledCenter).add(direction.multiplyScalar(finalDistance));
    this.controls.target.copy(scaledCenter);
    this.controls.update();
  }

  _computeLoadedModelsBounds() {
    if (!this.contentGroup || this.loadedModels.length === 0) return null;

    this.contentGroup.updateMatrixWorld(true);

    const boundingBox = new THREE.Box3();
    let validCount = 0;

    for (const model of this.loadedModels) {
      const modelBox = new THREE.Box3().setFromObject(model);
      if (!modelBox.isEmpty() && isFinite(modelBox.min.x) && isFinite(modelBox.max.x) &&
          isFinite(modelBox.min.y) && isFinite(modelBox.max.y) &&
          isFinite(modelBox.min.z) && isFinite(modelBox.max.z)) {
        boundingBox.union(modelBox);
        validCount++;
      }
    }

    if (boundingBox.isEmpty() || validCount === 0) return null;
    return boundingBox;
  }

  centerContentAtOrigin() {
    const boundingBox = this._computeLoadedModelsBounds();
    if (!boundingBox) return;

    const center = new THREE.Vector3();
    const size = new THREE.Vector3();
    boundingBox.getCenter(center);
    boundingBox.getSize(size);

    if (this._precomputedScale) {
      this.contentGroup.position.set(0, -boundingBox.min.y, 0);
    } else {
      const maxDim = Math.max(size.x, size.y, size.z);
      const targetSize = 1500;
      const minScale = 0.0001;
      const maxScale = 10000;
      let scale = maxDim > 0 ? targetSize / maxDim : 1;
      scale = Math.max(minScale, Math.min(maxScale, scale));

      if (!isFinite(scale) || !isFinite(center.x)) {
        return;
      }

      this.contentGroup.scale.setScalar(scale);
      this.contentGroup.position.set(0, -boundingBox.min.y * scale, 0);
    }
  }

  applyWorldOrientation() {
    if (!this.contentGroup || !this.currentNode?.worldRot) return;

    const worldRot = this.currentNode.worldRot;
    const quaternion = new THREE.Quaternion(worldRot.x, worldRot.y, worldRot.z, worldRot.w);

    // Extract just the yaw (rotation around Y) - ignore tilt from Earth's curvature
    const euler = new THREE.Euler().setFromQuaternion(quaternion, 'YXZ');
    this.contentGroup.rotation.y = euler.y;
  }

  animateCameraToContent() {
    const boundingBox = this._computeLoadedModelsBounds();
    if (!boundingBox) {
      this.resetCamera();
      return;
    }

    const center = new THREE.Vector3();
    const size = new THREE.Vector3();
    boundingBox.getCenter(center);
    boundingBox.getSize(size);

    const finalDistance = this._cameraDistanceForSize(size);
    if (!isFinite(finalDistance) || !isFinite(center.x)) {
      this.resetCamera();
      return;
    }

    this._positionGroundPlane(boundingBox.min.y);

    const direction = new THREE.Vector3(1, 0.5, 1).normalize();
    const targetPosition = center.clone().add(direction.multiplyScalar(finalDistance));
    this.animateCamera(targetPosition, center);
  }

  fitCameraToContent() {
    const boundingBox = this._computeLoadedModelsBounds();
    if (!boundingBox) {
      this.resetCamera();
      return;
    }

    const center = new THREE.Vector3();
    const size = new THREE.Vector3();
    boundingBox.getCenter(center);
    boundingBox.getSize(size);

    const finalDistance = this._cameraDistanceForSize(size);
    if (!isFinite(finalDistance) || !isFinite(center.x)) {
      this.resetCamera();
      return;
    }

    const direction = new THREE.Vector3(1, 0.5, 1).normalize();
    this.camera.position.copy(center).add(direction.multiplyScalar(finalDistance));
    this.controls.target.copy(center);
    this.controls.update();

    this._positionGroundPlane(boundingBox.min.y);
    this._fitShadowCamera(size, center);
  }

  _fitShadowCamera(size, center) {
    if (!this.keyLight) return;

    const maxDim = Math.max(size.x, size.y, size.z);
    const halfExtent = maxDim * 0.75;
    const shadow = this.keyLight.shadow;

    shadow.camera.left = -halfExtent;
    shadow.camera.right = halfExtent;
    shadow.camera.top = halfExtent;
    shadow.camera.bottom = -halfExtent;

    const lightDistance = halfExtent * 2;
    shadow.camera.near = lightDistance - halfExtent;
    shadow.camera.far = lightDistance + halfExtent;
    shadow.camera.updateProjectionMatrix();

    const lightDir = this.keyLight.position.clone().sub(center).normalize();
    if (lightDir.length() < 0.01) lightDir.set(1, 1, 1).normalize();
    this.keyLight.position.copy(center).add(lightDir.multiplyScalar(lightDistance));
    this.keyLight.target.position.copy(center);
    this.keyLight.target.updateMatrixWorld();

    shadow.needsUpdate = true;
  }

  _cameraDistanceForSize(size) {
    const maxDim = Math.max(size.x, size.y, size.z);
    const fov = this.camera.fov * (Math.PI / 180);
    const distance = (maxDim / 2) / Math.tan(fov / 2) * 1.5;
    return Math.max(5, Math.min(5000, distance));
  }

  _positionGroundPlane(minY) {
    if (!isFinite(minY)) return;
    if (this.gridHelper) this.gridHelper.position.y = minY;
    if (this.shadowPlane) this.shadowPlane.position.y = minY - 0.01;
  }

  resetCamera() {
    this.controls.target.set(0, 0, 0);
    this.camera.position.set(50, 30, 50);
    this.controls.update();
    this._positionGroundPlane(0);
  }

  clearScene() {
    // Invalidate both active callbacks and semaphore waiters before detaching groups.
    ++this.loadRequestId;
    clearTimeout(this._setNodeDebounce);
    this.isLoading = false;
    this.currentRootKey = null;
    this.currentResourceUrl = null;
    if (!this.initialized) return;

    this._precomputedScale = false;

    if (this.contentGroup) {
      this.scene.remove(this.contentGroup);
    }
    this.clearBoundsGroup();
    this._positionGroundPlane(0);
    // One teardown owns all scene and template assets, including shared clones.
    const disposedAssets = new Set();
    this.loadedModels.forEach(model => this._disposeLoadedObject(model, false, disposedAssets));
    this.loadedModels = [];
    this.contentGroup = null;
    this.nodeGroups.clear();
    this.nodeResourceGroups.clear();
    this.rotators = [];

    // Clean up video elements and HLS instances
    for (const mesh of [...this.videoPlanes]) this._releaseVideoPlane(mesh);

    for (const hls of this.hlsInstances) {
      hls.destroy();
    }
    this.hlsInstances = [];

    // Clear and dispose cached GLB scenes
    for (const [, cachedScene] of this.glbCache) {
      this._disposeLoadedObject(cachedScene, false, disposedAssets);
    }
    this.glbCache.clear();
    this.metadataCache.clear();
  }

  // Share the transient disposal set across a full teardown to free cloned assets once.
  _disposeLoadedObject(object, preserveCached = false, disposedAssets = new Set()) {
    this.rotators = this.rotators.filter(rotator => !this._isInGroup(rotator.target, object));
    object.traverse(child => {
      if (child.userData?.isVideoPlane) this._releaseVideoPlane(child);
      if (child.geometry && !disposedAssets.has(child.geometry) &&
          (!preserveCached || child.geometry[cachedAssetOwner] !== this)) {
        disposedAssets.add(child.geometry);
        child.geometry.dispose();
      }
      if (child.material) {
        const materials = Array.isArray(child.material) ? child.material : [child.material];
        materials.forEach(material => {
          if (!disposedAssets.has(material) && (!preserveCached || material[cachedAssetOwner] !== this)) {
            disposedAssets.add(material);
            this.disposeMaterial(material);
          }
        });
      }
    });
  }

  _releaseVideoPlane(mesh) {
    const { video, hls, releaseVideoListeners } = mesh.userData;
    releaseVideoListeners?.();
    if (hls) hls.destroy();
    if (video) {
      video.pause();
      video.src = '';
      video.load();
    }
    this.videoPlanes = this.videoPlanes.filter(plane => plane !== mesh);
    this.hlsInstances = this.hlsInstances.filter(instance => instance !== hls);
    mesh.userData.video = null;
    mesh.userData.hls = null;
    mesh.userData.releaseVideoListeners = null;
  }

  disposeMaterial(material) {
    if (material.map) material.map.dispose();
    if (material.normalMap) material.normalMap.dispose();
    if (material.roughnessMap) material.roughnessMap.dispose();
    if (material.metalnessMap) material.metalnessMap.dispose();
    if (material.aoMap) material.aoMap.dispose();
    if (material.emissiveMap) material.emissiveMap.dispose();
    if (material.lightMap) material.lightMap.dispose();
    if (material.envMap) material.envMap.dispose();
    if (material.alphaMap) material.alphaMap.dispose();
    if (material.bumpMap) material.bumpMap.dispose();
    if (material.displacementMap) material.displacementMap.dispose();
    if (material.specularMap) material.specularMap.dispose();
    material.dispose();
  }

  dispose() {
    this.disposed = true;

    // Stop animation loop
    if (this.animationFrameId) {
      cancelAnimationFrame(this.animationFrameId);
      this.animationFrameId = null;
    }

    // Stop camera animation
    if (this.cameraAnimationId) {
      cancelAnimationFrame(this.cameraAnimationId);
      this.cameraAnimationId = null;
    }

    // Remove event listeners
    window.removeEventListener('resize', this.boundResizeHandler);
    document.removeEventListener('visibilitychange', this.boundVisibilityHandler);
    if (this.renderer?.domElement) {
      this.renderer.domElement.removeEventListener('dblclick', this.boundDblClickHandler);
      this.renderer.domElement.removeEventListener('click', this.boundClickHandler);
    }

    // Remove bounds toggle listener
    const toggle = document.getElementById('resource-bounds-toggle');
    if (toggle && this.boundBoundsToggleHandler) {
      toggle.removeEventListener('change', this.boundBoundsToggleHandler);
    }

    // Remove time slider listeners
    if (this.timeSlider && this.boundTimeSliderHandler) {
      this.timeSlider.removeEventListener('input', this.boundTimeSliderHandler);
    }
    if (this.boundTimeInputHandler) {
      this.timeHourInput?.removeEventListener('change', this.boundTimeInputHandler);
      this.timeMinuteInput?.removeEventListener('change', this.boundTimeInputHandler);
    }

    // Clear scene content
    this.clearScene();

    // Dispose scene helpers
    for (const obj of [this.sky, this.skyDome, this.gridHelper, this.shadowPlane, this.starfield]) {
      if (obj) {
        obj.geometry?.dispose();
        obj.material?.dispose();
      }
    }

    // Dispose lights
    this.keyLight?.dispose();

    // Dispose environment
    this.scene?.environment?.dispose();
    this.pmremGenerator?.dispose();

    // Dispose loaders, controls, and renderer
    this.dracoLoader?.dispose();
    this.ktx2Loader?.dispose();
    this.controls?.dispose();
    this.renderer?.dispose();
  }

  setStatus(message, state = '') {
    const statusEl = this.container?.querySelector('.resource-status');
    if (statusEl) {
      statusEl.textContent = message;
      statusEl.className = 'resource-status';
      if (state) statusEl.classList.add(state);
    }
    this.statusCallbacks.forEach(cb => cb(message, state));
  }

  onStatus(callback) {
    this.statusCallbacks.push(callback);
  }

  offStatus(callback) {
    const index = this.statusCallbacks.indexOf(callback);
    if (index !== -1) {
      this.statusCallbacks.splice(index, 1);
    }
  }

  updateGridFromContent() {
    if (!this.gridHelper || !this.contentGroup || this.loadedModels.length === 0) {
      return;
    }

    const boundingBox = new THREE.Box3();
    for (const model of this.loadedModels) {
      const modelBox = new THREE.Box3().setFromObject(model);
      if (!modelBox.isEmpty() && isFinite(modelBox.min.x)) {
        boundingBox.union(modelBox);
      }
    }

    if (boundingBox.isEmpty()) {
      return;
    }

    const size = new THREE.Vector3();
    boundingBox.getSize(size);
    const characteristicSize = Math.cbrt(size.x * size.y * size.z);
    const spacing = calculateGridSpacing(characteristicSize);

    // Fade distance based on furthest extent from origin
    const center = new THREE.Vector3();
    boundingBox.getCenter(center);
    const maxExtent = Math.max(
      Math.abs(boundingBox.min.x), Math.abs(boundingBox.max.x),
      Math.abs(boundingBox.min.z), Math.abs(boundingBox.max.z)
    );
    spacing.fadeDistance = maxExtent;

    updateGridSpacing(this.gridHelper, spacing);
  }

  setupBoundsToggle() {
    const toggle = document.getElementById('resource-bounds-toggle');
    if (!toggle) return;

    // Restore saved state
    const state = this.stateManager?.getSection('viewResource') || {};
    if (typeof state.showBounds === 'boolean') {
      this.showBounds = state.showBounds;
      toggle.checked = state.showBounds;
    }

    this.boundBoundsToggleHandler = () => {
      this.showBounds = toggle.checked;
      this.updateBoundsDisplay();
      this.saveState();
    };
    toggle.addEventListener('change', this.boundBoundsToggleHandler);
  }

  saveState() {
    if (!this.stateManager) return;
    this.stateManager.updateSection('viewResource', {
      showBounds: this.showBounds
    });
  }

  restoreState(state) {
    state = state || this.stateManager?.getSection('viewResource') || {};

    if (typeof state.showBounds === 'boolean') {
      this.showBounds = state.showBounds;
      const toggle = document.getElementById('resource-bounds-toggle');
      if (toggle) {
        toggle.checked = state.showBounds;
      }
      this.updateBoundsDisplay();
    }
  }

  updateBoundsDisplay() {
    this.clearBoundsGroup();

    if (!this.showBounds || !this.contentGroup || !this.currentNode) return;

    this.boundsGroup = new THREE.Group();
    this.boundsGroup.name = 'boundsGroup';

    this._addBoundsRecursive(this.currentNode, this.boundsGroup, true);

    this.contentGroup.add(this.boundsGroup);
  }

  _addBoundsRecursive(node, parentGroup, isRoot = false) {
    const group = new THREE.Group();

    if (!isRoot && node.transform) {
      this._applyNodeTransformToGroup(group, node.transform);
    }

    if (node.bound) {
      const box = this.createNodeBoundingBox(node);
      if (box) {
        group.add(box);
      }
    }

    if (this.model.isNodeExpanded(node) && node.children) {
      for (const child of node.children) {
        this._addBoundsRecursive(child, group);
      }
    }

    parentGroup.add(group);
  }

  clearBoundsGroup() {
    if (!this.boundsGroup) return;

    this.boundsGroup.traverse((child) => {
      if (child.geometry) {
        child.geometry.dispose();
      }
      if (child.material) {
        if (child.material.map) {
          child.material.map.dispose();
        }
        child.material.dispose();
      }
    });
    if (this.boundsGroup.parent) {
      this.boundsGroup.parent.remove(this.boundsGroup);
    }
    this.boundsGroup = null;
  }

  createNodeBoundingBox(node) {
    const bound = node.bound;
    if (!bound || bound.x == null || bound.y == null || bound.z == null) return null;

    const color = this.getNodeColor(node);
    const group = new THREE.Group();
    group.name = `bounds-${node.name || 'node'}`;

    const geometry = new THREE.BoxGeometry(bound.x * 2, bound.y, bound.z * 2);
    const edges = new THREE.EdgesGeometry(geometry);
    const material = new THREE.LineBasicMaterial({ color: color });
    const wireframe = new THREE.LineSegments(edges, material);

    wireframe.position.y = bound.y / 2;

    group.add(wireframe);

    const labelHeight = Math.max(bound.x, bound.y, bound.z) * 0.08;
    const label = this.createBoundsLabel(node.name || 'Node', color, labelHeight);
    label.position.set(0, bound.y + labelHeight * 0.6, 0);
    group.add(label);

    geometry.dispose();

    return group;
  }

  createBoundsLabel(text, color, worldHeight) {
    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');
    const fontSize = 48;
    const padding = 8;

    ctx.font = `bold ${fontSize}px Arial`;
    const metrics = ctx.measureText(text);
    const textWidth = metrics.width;

    canvas.width = Math.ceil(textWidth) + padding * 2;
    canvas.height = fontSize + padding * 2;

    // Background
    ctx.fillStyle = 'rgba(0, 0, 0, 0.1)';
    ctx.fillRect(0, 0, canvas.width, canvas.height);

    // Text
    ctx.font = `bold ${fontSize}px Arial`;
    ctx.fillStyle = '#' + new THREE.Color(color).getHexString();
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';
    ctx.fillText(text, canvas.width / 2, canvas.height / 2);

    const texture = new THREE.CanvasTexture(canvas);
    texture.needsUpdate = true;

    const material = new THREE.SpriteMaterial({
      map: texture,
      transparent: true,
      depthTest: false,
      depthWrite: false
    });

    const sprite = new THREE.Sprite(material);

    // Scale sprite based on requested world height, maintaining aspect ratio
    const aspectRatio = canvas.width / canvas.height;
    sprite.scale.set(worldHeight * aspectRatio, worldHeight, 1);

    return sprite;
  }

  getNodeColor(node) {
    if (!node.nodeType) return 0xffffff;
    return NODE_COLORS[node.nodeType] ?? 0xffffff;
  }
}
